import {
  ArrowDown01Icon,
  Cancel01Icon,
  File02Icon
} from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { Disclosure } from "@headlessui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { useMemo, useState } from "react";
import {
  Button,
  ButtonVariant,
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  Show,
  TruncateWithTooltip
} from "shared/components";
import { cx } from "shared/utils";
import { getFullName } from "shared/utils/get-full-name";
import { Applicant } from "types/application";
import { InformationCircleIcon } from "@hugeicons-pro/core-stroke-rounded";
import type { BankStatements, EditTransactionsModalData } from "../../types";
import { getAvailableBankStatements } from "./utils";
import { toast } from "react-toastify";
import { Banner } from "shared/components/Banner";
import { getIncomeJobName, incomeTypeMap } from "../../utils";
import { CombinedIncome } from "../../types/income";
import { getErrorMessage } from "utils/error";
import { Step } from "./types";
import { useEditViewController } from "./controllers/useEditViewController";
import { EditView } from "./components/EditView";
import { ConfirmationView } from "./components/ConfirmationView";

type Props = Partial<EditTransactionsModalData> & {
  onClose: () => void;
  applicant: Applicant;
  verificationId: number;
  applicationId: number;
  income?: CombinedIncome;
  allBankStatements?: BankStatements;
  onSuccess?: () => void;
};

export const EditTransactions = ({
  onClose,
  applicant,
  verificationId,
  applicationId,
  allBankStatements,
  onSuccess,
  id: incomeId,
  additional_data,
  type
}: Props) => {
  const queryClient = useQueryClient();
  const [step, setStep] = useState<Step>("selection");
  const isConfirmation = step === "confirmation";

  const { filteredBankStatements, filteredSourceIds } = useMemo(() => {
    const filteredBankStatements =
      getAvailableBankStatements(allBankStatements);
    const filteredSourceIds = filteredBankStatements.map((entry) => entry.id);

    return {
      filteredBankStatements,
      filteredSourceIds
    };
  }, [allBankStatements]);

  const editViewData = useEditViewController({
    applicationId,
    verificationId,
    applicant,
    incomeId,
    filteredSourceIds
  });

  const { mutate, isPending } = useMutation({
    mutationKey: [
      "EDIT_TRANSACTIONS",
      applicationId,
      verificationId,
      applicant.id,
      incomeId
    ],
    mutationFn: (itemIds: number[]) => {
      return instance
        .post(
          `/portal/v1/applications/${applicationId}/verifications/${verificationId}/income/${incomeId}/assign_items`,
          { applicant_id: applicant.id, item_ids: itemIds }
        )
        .then(({ data }) => data);
    },
    onError: (err) => toast.error(getErrorMessage(err)),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["APPLICATION", applicationId]
      });
      queryClient.invalidateQueries({
        queryKey: ["APPLICATION_BANK_STATEMENT_TRANSACTIONS"]
      });
      queryClient.invalidateQueries({
        queryKey: ["APPLICATION_BANK_STATEMENT_TRANSACTIONS_ASSIGNED"]
      });
      queryClient.invalidateQueries({
        queryKey: ["APPLICATION_BANK_STATEMENT_TRANSACTIONS_CONFIRMATION"]
      });
      toast.success("Income was successfully updated");
      onSuccess?.();
      onClose();
    }
  });

  const handleSave = () => {
    mutate(editViewData.selectionIds);
  };

  return (
    <div className="h-fit max-h-[80vh] flex flex-col rounded-lg bg-white">
      <header className="px-6 py-4 min-h-[80px] rounded-t-lg border-b border-b-alpha-40">
        <div className="text-xl font-bold flex justify-between items-center leading-8">
          Edit income transactions
          <IconButton
            HugeIcon={Cancel01Icon}
            size={IconButtonSize.LG}
            variant={IconButtonVariant.TEXT_BASE}
            onClick={onClose}
          />
        </div>
        <p className="flex items-center gap-2 text-content-400 font-medium text-sm leading-5">
          {getFullName({
            firstName: applicant.first_name,
            lastName: applicant.last_name,
            middleName: applicant.middle_name,
            short: true
          })}
          {additional_data ? (
            <>
              <span className="block size-1.5 shrink-0 bg-content-100 rounded-full" />
              {getIncomeJobName(additional_data)}
            </>
          ) : null}
          {type ? (
            <>
              <span className="block size-1.5 shrink-0 bg-content-100 rounded-full" />
              {incomeTypeMap[type]}
            </>
          ) : null}
        </p>
      </header>
      <div className="flex-grow overflow-y-auto flex flex-col">
        <Show if={editViewData.hasSelectionChanged}>
          <Banner
            variant="warning"
            textTheme="base"
            borders="y-axis"
            body="Editing income amount may affect all summary metrics and decision
            recommendations"
            HugeIcon={
              <HugeiconsIcon
                icon={InformationCircleIcon}
                className="shrink-0 text-warning-500"
                size="20"
              />
            }
          />
        </Show>
        <Show if={filteredBankStatements.length}>
          <Disclosure>
            {({ open }) => (
              <div className="border-b border-alpha-100 bg-white">
                <Disclosure.Button className="px-6 py-5 flex items-center justify-between w-full">
                  <h2 className="text-base font-bold text-content-1000">
                    Uploaded bank statements
                    <span className="text-content-300 font-medium ml-2">
                      {filteredBankStatements.length} file
                      {filteredBankStatements.length > 1 ? "s" : ""}
                    </span>
                  </h2>
                  <HugeiconsIcon
                    icon={ArrowDown01Icon}
                    size={20}
                    className={cx(
                      "text-content-500 transition-transform",
                      open && "rotate-180"
                    )}
                  />
                </Disclosure.Button>
                <Disclosure.Panel className="px-6 pb-5">
                  <div className="grid grid-cols-[repeat(auto-fit,minmax(160px,1fr))] gap-4 max-w-[864px]">
                    {filteredBankStatements.map(({ id, statement }, idx) => (
                      <div
                        className="flex items-center gap-2 min-w-0"
                        key={`bank-statement-${id}`}
                      >
                        <HugeiconsIcon
                          icon={File02Icon}
                          size={20}
                          className="text-content-100 shrink-0"
                        />
                        <TruncateWithTooltip
                          content={
                            statement.asset?.asset?.filename ||
                            `Statement #${idx}`
                          }
                          tooltipClassName="z-[1000] border-alpha-40"
                          className="text-sm font-medium text-content-1000"
                        />
                      </div>
                    ))}
                  </div>
                </Disclosure.Panel>
              </div>
            )}
          </Disclosure>
        </Show>
        {isConfirmation ? (
          <ConfirmationView
            applicationId={applicationId}
            verificationId={verificationId}
            applicant={applicant}
            incomeId={incomeId}
            selectionIds={editViewData.selectionIds}
          />
        ) : (
          <EditView edit={editViewData} incomeId={incomeId} />
        )}
      </div>

      <footer className="flex gap-2 justify-end p-4 border-t border-alpha-100">
        {isConfirmation ? (
          <>
            <Button
              className="w-full md:max-w-[125px]"
              onClick={() => setStep("selection")}
            >
              Back
            </Button>
            <Button
              className="w-full md:max-w-[125px]"
              variant={ButtonVariant.PRIMARY}
              onClick={handleSave}
              loading={isPending}
            >
              Save changes
            </Button>
          </>
        ) : (
          <>
            <Button className="w-full md:max-w-[125px]" onClick={onClose}>
              Cancel
            </Button>
            <Button
              className="w-full md:max-w-[125px]"
              variant={ButtonVariant.PRIMARY}
              disabled={
                !editViewData.hasSelectionChanged ||
                editViewData.selectionIds.length === 0
              }
              onClick={() => setStep("confirmation")}
            >
              Continue
            </Button>
          </>
        )}
      </footer>
    </div>
  );
};
